import React, { useEffect, useState, createContext, useContext } from 'react';
type ConsentStatus = 'pending' | 'accepted' | 'rejected';
interface CookieConsentContextType {
  consentStatus: ConsentStatus;
  acceptCookies: () => void;
  rejectCookies: () => void;
  resetConsent: () => void;
  hasConsent: boolean;
}
const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);
const CONSENT_STORAGE_KEY = 'cookie_consent';
export const CookieConsentProvider: React.FC<{
  children: ReactNode;
}> = ({
  children
}) => {
  const [consentStatus, setConsentStatus] = useState<ConsentStatus>('pending');
  useEffect(() => {
    const storedConsent = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (storedConsent === 'accepted' || storedConsent === 'rejected') {
      setConsentStatus(storedConsent);
      // Push consent status to dataLayer
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'consent_status',
          consent_granted: storedConsent === 'accepted'
        });
      }
    }
  }, []);
  const acceptCookies = () => {
    setConsentStatus('accepted');
    localStorage.setItem(CONSENT_STORAGE_KEY, 'accepted');
    // Push consent granted event to dataLayer
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'consent_granted',
        consent_granted: true
      });
    }
  };
  const rejectCookies = () => {
    setConsentStatus('rejected');
    localStorage.setItem(CONSENT_STORAGE_KEY, 'rejected');
    // Push consent rejected event to dataLayer
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'consent_rejected',
        consent_granted: false
      });
    }
  };
  // Add reset function for development/testing
  const resetConsent = () => {
    setConsentStatus('pending');
    localStorage.removeItem(CONSENT_STORAGE_KEY);
  };
  return <CookieConsentContext.Provider value={{
    consentStatus,
    acceptCookies,
    rejectCookies,
    resetConsent,
    hasConsent: consentStatus === 'accepted'
  }} data-id="element-1496">
      {children}
    </CookieConsentContext.Provider>;
};
export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};
// Global helper function for development - accessible from browser console
if (typeof window !== 'undefined') {
  ;
  (window as any).resetCookieConsent = () => {
    localStorage.removeItem('cookie_consent');
    window.location.reload();
  };
}
// Extend Window interface for dataLayer
declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}